import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, RadioGroup } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface EvidenceItem {
  id: string;
  item: string;
  dibb: string;
  evidence: string;
  confidence: string;
  learningSlice: string;
}

export default function EvidenceCheckPage() {
  const [items, setItems] = useState<EvidenceItem[]>([
    { id: '1', item: '', dibb: '', evidence: '', confidence: '', learningSlice: '' },
  ]);

  const updateItem = (id: string, field: keyof EvidenceItem, value: string) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addItem = () => {
    setItems([...items, { id: Date.now().toString(), item: '', dibb: '', evidence: '', confidence: '', learningSlice: '' }]);
  };

  return (
    <TaskPage
      phase="arrange"
      title="Evidence Check"
      pageNumber={3}
      instruction="Sanity before ranking. Check the evidence for each item. Rule of thumb: if confidence is Low, capture the learning slice you'd run first (≤2 weeks)."
      whyItMatters="Brief, structured checks function as scaffolds—novices don't need deep statistics to spot thin evidence."
      coachRail={{
        timing: "10 minutes",
        tips: "If confidence is Low, define a learning slice (≤2 weeks) to test the assumption.",
        debrief: "Which items have weak evidence? What should we test first?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--surface)] p-6 overflow-x-auto">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Evidence Check Table</h3>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[var(--hover-tint)] border-b-2 border-[var(--rule)]">
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Item</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Our DIBB</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">One-line evidence</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Confidence</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Learning slice (if Low)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-[var(--rule)]">
                  <td className="p-2">
                    <TextInput
                      value={item.item}
                      onChange={(e) => updateItem(item.id, 'item', e.target.value)}
                      placeholder="Item name"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'Data', label: 'Data' },
                        { value: 'Insight', label: 'Insight' },
                        { value: 'Belief', label: 'Belief' },
                        { value: 'Bet', label: 'Bet' }
                      ]}
                      value={item.dibb}
                      onChange={(value) => updateItem(item.id, 'dibb', value)}
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.evidence}
                      onChange={(e) => updateItem(item.id, 'evidence', e.target.value)}
                      placeholder="One-line evidence"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'H', label: 'H' },
                        { value: 'M', label: 'M' },
                        { value: 'L', label: 'L' }
                      ]}
                      value={item.confidence}
                      onChange={(value) => updateItem(item.id, 'confidence', value)}
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.learningSlice}
                      onChange={(e) => updateItem(item.id, 'learningSlice', e.target.value)}
                      placeholder="If Low: ≤2 week test"
                      className="text-xs"
                      disabled={item.confidence !== 'L'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            onClick={addItem}
            className="mt-4 w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
          >
            + Add another item
          </button>
        </div>

        <Callout type="tip" phase="arrange">
          <p className="text-sm">If confidence is Low, define a learning slice (≤2 weeks) to test the assumption before committing.</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






